import { useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  Archive,
  BookOpen,
  BriefcaseBusiness,
  Camera,
  ChevronDown,
  CheckCircle2,
  ClipboardCheck,
  Copy,
  Database,
  FileText,
  Fingerprint,
  FolderKanban,
  Gauge,
  Globe2,
  ExternalLink,
  Mic,
  Menu,
  PenLine,
  Plus,
  Printer,
  RefreshCw,
  Save,
  Send,
  ShieldCheck,
  Settings,
  TabletSmartphone,
  Trash2,
  UploadCloud
} from "lucide-react";
import { departments, findNormReference, normativeReferences, roadmap, templates, type AuditField, type AuditTemplate, type NormReference } from "./templates";
import {
  loadAssignments,
  loadAppSettings,
  loadAuditorProfiles,
  loadAudits,
  newAssignment,
  newAudit,
  newAuditorProfile,
  newExternalRepository,
  repositoryProviderLabel,
  saveAssignments,
  saveAppSettings,
  saveAuditorProfiles,
  saveAudits,
  type AppLanguage,
  type AppSettings,
  type AuditAssignment,
  type AuditRecord,
  type AuditorProfile,
  type ExternalRepository,
  type RepositoryProvider,
  type RepositoryStatus
} from "./storage";

const coreNav = [
  { id: "assignment", labelKey: "assignmentNav", icon: BriefcaseBusiness },
  { id: "dashboard", label: "Audit Console", icon: Gauge },
  { id: "norms", labelKey: "normativeReferences", icon: BookOpen },
  { id: "reports", label: "Report", icon: FileText },
  { id: "settings", labelKey: "settingsNav", icon: Settings },
  { id: "sync", label: "Cloudflare", icon: UploadCloud }
] as const;

type View = (typeof coreNav)[number]["id"] | "forms";
type RiskLevel = AuditRecord["risk"];

const inspectionOrder = [
  "iso-questionnaire",
  "ecovadis-isps-esg",
  "terminal-sira-risk",
  "port-operations-risk",
  "terminal-safety-controls",
  "terminal-security-isps",
  "terminal-environmental-controls",
  "terminal-medical-readiness",
  "terminal-people-fatigue",
  "terminal-it-cyber-cctv",
  "sire-viq",
  "q88-oil-chemical",
  "lng-q88"
];
const inspectionTemplates = inspectionOrder.map((id) => templates.find((template) => template.id === id)).filter(Boolean) as AuditTemplate[];
const inheritedFieldIds = new Set(["assignment_label", "client", "country", "port_terminal", "vessel", "scope", "inspection_start", "inspection_end", "auditor_profile_id", "report_date", "audit_period", "place", "auditor", "audit_scope", "port_name", "local_agency", "address"]);
const repositoryProviders: RepositoryProvider[] = ["google-drive", "dropbox", "onedrive", "mega", "other"];
const repositoryStatuses: RepositoryStatus[] = ["Not connected", "Needs configuration", "Connected"];

const criticalityPresets: Record<string, { severity: number; probability: number; ci: number; risk: RiskLevel }> = {
  "Negligible risk <5": { severity: 2, probability: 2, ci: 4, risk: "Low" },
  "Risk to follow [5,15]": { severity: 3, probability: 3, ci: 9, risk: "Medium" },
  "Risk to treat >15": { severity: 4, probability: 4, ci: 16, risk: "Critical" }
};

const auditRiskPresets: Record<RiskLevel, { severity: number; probability: number; ci: number; level: string }> = {
  Low: { severity: 2, probability: 2, ci: 4, level: "Negligible risk <5" },
  Medium: { severity: 3, probability: 3, ci: 9, level: "Risk to follow [5,15]" },
  High: { severity: 3, probability: 5, ci: 15, level: "Risk to follow [5,15]" },
  Critical: { severity: 4, probability: 4, ci: 16, level: "Risk to treat >15" }
};

const UI = {
  en: {
    assignmentNav: "Audit Assignment",
    settingsNav: "Auditor Settings",
    inspectionAreas: "Inspection Areas",
    operations: "Operations",
    offline: "Offline-first PWA",
    masterRecord: "Master record",
    auditConsole: "Audit Console",
    auditWorkspace: "Audit Workspace",
    newAudit: "New audit",
    countryUnset: "Country not set",
    portUnset: "Port / Terminal not set",
    auditorUnset: "Auditor not set",
    area: "Area",
    team: "Team",
    setRisk: "Set current record risk",
    setStatus: "Set current record status",
    assignment: "Assignment",
    client: "Client",
    period: "Period",
    editAssignment: "Edit assignment",
    whoWhatWhenWhere: "Who, what, when, where",
    parentContext: "Define the parent context: these data are inherited by Audit Console, roadmap and Inspection Areas.",
    goConsole: "Go to Audit Console",
    assignmentName: "Audit assignment name",
    createAssignment: "Create a new audit assignment",
    duplicateAssignment: "Duplicate current audit assignment",
    deleteAssignment: "Delete current audit assignment",
    saveAssignment: "Save audit assignment",
    newButton: "New",
    duplicate: "Duplicate",
    delete: "Delete",
    save: "Save",
    assignmentsList: "Audit assignments",
    openAssignment: "Open assignment",
    saved: "Saved",
    clientOrg: "Client / organization",
    clientPlaceholder: "Company / client / organization",
    country: "Country",
    portTerminal: "Name of Port / Terminal",
    portPlaceholder: "Port / terminal / facility",
    vesselAsset: "Vessel / asset",
    optional: "Optional",
    auditorTeam: "Auditor team",
    toggleAuditor: "Add or remove auditor from the assignment team",
    assignmentStatus: "Assignment status",
    startDate: "Inspection start date",
    endDate: "Inspection end date",
    scope: "Scope / what we need to do",
    scopePlaceholder: "Audit scope, criteria, applicable standards, boundaries and assignment objective",
    roadmapTitle: "ISO 19011 risk-based roadmap",
    standardCoverage: "Standard coverage",
    processes: "Processes and departments",
    areaOwner: "Area lead auditor",
    assignArea: "Assign this Inspection Area to an auditor in the team",
    recordTitle: "Record title",
    createRecord: "Create a new record for this questionnaire",
    duplicateRecord: "Duplicate current record",
    deleteRecord: "Delete current record",
    validateSave: "Validate and save questionnaire",
    preview: "Preview",
    print: "Print",
    forward: "Forward",
    allSections: "All sections",
    selectedRecords: "Selected form records",
    siteUnset: "Site not set",
    inherited: "Inherited from assignment",
    narrativePlaceholder: "Write here the finding, reason, evidence, reference and requested action",
    noInspectionOpen: "No Inspection Area open for this assignment",
    noInspectionHelp: "Open the Inspection Areas menu and select a module: a record linked to the current audit assignment will be created.",
    openInspection: "Open Inspection Areas",
    previewQuestionnaire: "Questionnaire preview",
    close: "Close",
    dedicatedReportBody: "Dedicated report generated from questionnaire fields, with observations, non-conformities, inline evidence and auditor data.",
    openQuestionnaire: "Open questionnaire",
    reportPreview: "Report preview",
    normativeReferences: "Normative References",
    normativeSubtitle: "Referenced rules and guidance linked to checklist fields, findings and final reports.",
    normButton: "Norm",
    normTitle: "Open normative reference",
    normReference: "Normative reference",
    sourceUrl: "Source URL",
    guidance: "Guidance",
    searchNorms: "Search rules, standards or domains",
    allCategories: "All categories",
    linkedFields: "Linked fields",
    openLinkedField: "Open checklist field",
    noNorms: "No normative references found",
    auditorSettings: "Auditor settings",
    languageSettings: "Language settings",
    platformLanguage: "Platform language",
    onlineSettings: "Online platform settings",
    onlineSettingsBody: "Move data from browser-only storage to Cloudflare D1, protect access with Cloudflare and archive evidence in the selected external repository.",
    cloudMode: "Data mode",
    localFirstMode: "Local-first fallback",
    cloudOnlineMode: "Cloud online",
    authMode: "User access",
    cloudflareAccessMode: "Cloudflare Access / Zero Trust",
    appLoginMode: "App login and password",
    externalRepositories: "External evidence repositories",
    externalRepositoriesBody: "Photos, documents, signatures and exported reports will be stored through the selected repository connector. OAuth secrets stay in Cloudflare, never in the browser.",
    primaryRepository: "Primary repository",
    repositoryProvider: "Repository provider",
    repositoryStatus: "Connection status",
    repositoryRootFolder: "Root folder",
    repositoryAccount: "Repository account email",
    repositoryClientId: "OAuth client ID",
    repositoryRedirect: "OAuth redirect URI",
    repositorySaved: "Repository settings saved.",
    repositoryReady: "Repository connector ready. Add OAuth credentials in Cloudflare secrets before connecting.",
    createRepository: "Create repository connector",
    duplicateRepository: "Duplicate repository connector",
    deleteRepository: "Delete repository connector",
    saveRepository: "Save repository connector",
    connectRepository: "Start repository connection",
    connectRepositoryHint: "OAuth connection will open after provider secrets are configured in Cloudflare.",
    english: "English",
    italian: "Italian",
    createAuditor: "Create a new auditor profile",
    duplicateAuditor: "Duplicate current auditor profile",
    deleteAuditor: "Delete current auditor profile",
    saveAuditor: "Save auditor profile",
    auditorProfiles: "Saved auditor profiles",
    openAuditor: "Open auditor profile",
    noName: "Name not set",
    firstName: "First name",
    lastName: "Last name",
    phone: "Phone",
    email: "Email",
    commands: "Commands",
    reloadApp: "Reload app",
    select: "Select",
    evidenceInline: "Inline evidence",
    photoTitle: "Add photo evidence",
    audioTitle: "Record or mark audio evidence",
    documentTitle: "Attach document evidence",
    geoTitle: "Capture geolocation",
    signatureTitle: "Sign this item",
    geoUnavailable: "Geolocation unavailable",
    geoDenied: "Geolocation not authorized",
    signed: "Signed",
    addFile: "Add file or capture evidence",
    tapSignature: "Tap to capture signature",
    completion: "Completion",
    metaAssignment: "Assignment",
    metaAreaAuditor: "Area auditor",
    status: "Status",
    risk: "Risk",
    savedMeta: "Saved",
    note: "Note",
    invalidAssignmentPeriod: "Invalid assignment period: inspection end date is before inspection start date.",
    outsidePeriod: "Date outside assignment period",
    mustBeBetween: "must be between",
    newAssignmentReady: "New audit assignment ready.",
    assignmentDuplicated: "Audit assignment duplicated.",
    assignmentDeleted: "Audit assignment deleted.",
    assignmentSaved: "Audit assignment saved. Data now flow down to the console and Inspection Areas.",
    newAuditorReady: "New auditor profile ready.",
    auditorDuplicated: "Auditor profile duplicated.",
    auditorDeleted: "Auditor profile deleted.",
    auditorSaved: "Auditor settings saved.",
    openBeforeSave: "Open or create an Inspection Area before saving the questionnaire.",
    requiredMissing: "Required fields missing",
    questionnaireSaved: "Questionnaire validated and saved."
  },
  it: {
    assignmentNav: "Commessa Audit",
    settingsNav: "Settings auditor",
    inspectionAreas: "Aree di ispezione",
    operations: "Operazioni",
    offline: "PWA offline-first",
    masterRecord: "Record padre",
    auditConsole: "Console Audit",
    auditWorkspace: "Workspace Audit",
    newAudit: "Nuovo audit",
    countryUnset: "Country non impostato",
    portUnset: "Porto / Terminal non impostato",
    auditorUnset: "Auditor non impostato",
    area: "Area",
    team: "Team",
    setRisk: "Imposta il rischio del record corrente",
    setStatus: "Imposta lo stato del record corrente",
    assignment: "Commessa",
    client: "Cliente",
    period: "Periodo",
    editAssignment: "Modifica commessa",
    whoWhatWhenWhere: "Chi, cosa, quando, dove",
    parentContext: "Definisci il contesto padre: questi dati vengono ereditati da Audit Console, roadmap e Aree di ispezione.",
    goConsole: "Vai alla Audit Console",
    assignmentName: "Nome commessa audit",
    createAssignment: "Crea una nuova commessa audit",
    duplicateAssignment: "Duplica la commessa audit corrente",
    deleteAssignment: "Elimina la commessa audit corrente",
    saveAssignment: "Salva la commessa audit",
    newButton: "Nuova",
    duplicate: "Duplica",
    delete: "Elimina",
    save: "Salva",
    assignmentsList: "Commesse audit",
    openAssignment: "Apri commessa",
    saved: "Salvata",
    clientOrg: "Cliente / organizzazione",
    clientPlaceholder: "Societa / cliente / organizzazione",
    country: "Country",
    portTerminal: "Name of Port / Terminal",
    portPlaceholder: "Porto / terminal / facility",
    vesselAsset: "Vessel / asset",
    optional: "Opzionale",
    auditorTeam: "Team auditor",
    toggleAuditor: "Aggiungi o rimuovi auditor dal team della commessa",
    assignmentStatus: "Stato commessa",
    startDate: "Data inizio ispezione",
    endDate: "Data fine ispezione",
    scope: "Scope / cosa dobbiamo fare",
    scopePlaceholder: "Audit scope, criteri, standard applicabili, limiti e obiettivo della commessa",
    roadmapTitle: "Roadmap ISO 19011 risk-based",
    standardCoverage: "Copertura standard",
    processes: "Processi e dipartimenti",
    areaOwner: "Auditor responsabile area",
    assignArea: "Assegna questa Area di ispezione a un auditor del team",
    recordTitle: "Titolo record",
    createRecord: "Crea un nuovo record per questo questionario",
    duplicateRecord: "Duplica il record corrente",
    deleteRecord: "Elimina il record corrente",
    validateSave: "Valida e salva questionario",
    preview: "Preview",
    print: "Stampa",
    forward: "Inoltra",
    allSections: "Tutte le sezioni",
    selectedRecords: "Record del form selezionato",
    siteUnset: "Sito non impostato",
    inherited: "Ereditato dalla commessa",
    narrativePlaceholder: "Scrivi qui rilievo, motivazione, evidenza, riferimento e azione richiesta",
    noInspectionOpen: "Nessuna Area di ispezione aperta per questa commessa",
    noInspectionHelp: "Apri il menu Aree di ispezione e scegli un modulo: verra creato un record collegato alla commessa audit corrente.",
    openInspection: "Apri Aree di ispezione",
    previewQuestionnaire: "Preview questionario",
    close: "Chiudi",
    dedicatedReportBody: "Report dedicato generato dai campi del questionario, con osservazioni, non conformita, evidenze inline e dati auditor.",
    openQuestionnaire: "Apri questionario",
    reportPreview: "Preview report",
    normativeReferences: "Riferimenti normativi",
    normativeSubtitle: "Norme e linee guida collegate ai campi checklist, ai finding e ai report finali.",
    normButton: "Norma",
    normTitle: "Apri riferimento normativo",
    normReference: "Riferimento normativo",
    sourceUrl: "URL fonte",
    guidance: "Guida",
    searchNorms: "Cerca norme, standard o domini",
    allCategories: "Tutte le categorie",
    linkedFields: "Campi collegati",
    openLinkedField: "Apri campo checklist",
    noNorms: "Nessun riferimento normativo trovato",
    auditorSettings: "Auditor settings",
    languageSettings: "Impostazioni lingua",
    platformLanguage: "Lingua piattaforma",
    onlineSettings: "Impostazioni piattaforma online",
    onlineSettingsBody: "Sposta i dati dallo storage solo-browser a Cloudflare D1, proteggi l'accesso con Cloudflare e archivia le evidenze nel repository esterno selezionato.",
    cloudMode: "Modalita dati",
    localFirstMode: "Fallback local-first",
    cloudOnlineMode: "Cloud online",
    authMode: "Accesso utenti",
    cloudflareAccessMode: "Cloudflare Access / Zero Trust",
    appLoginMode: "Login e password applicativi",
    externalRepositories: "Repository esterni evidenze",
    externalRepositoriesBody: "Foto, documenti, firme e report esportati verranno salvati tramite il connettore selezionato. I segreti OAuth restano in Cloudflare, mai nel browser.",
    primaryRepository: "Repository primario",
    repositoryProvider: "Provider repository",
    repositoryStatus: "Stato connessione",
    repositoryRootFolder: "Cartella radice",
    repositoryAccount: "Email account repository",
    repositoryClientId: "OAuth client ID",
    repositoryRedirect: "OAuth redirect URI",
    repositorySaved: "Repository salvato.",
    repositoryReady: "Connettore repository pronto. Aggiungi le credenziali OAuth nei secret Cloudflare prima del collegamento.",
    createRepository: "Crea connettore repository",
    duplicateRepository: "Duplica connettore repository",
    deleteRepository: "Elimina connettore repository",
    saveRepository: "Salva connettore repository",
    connectRepository: "Avvia collegamento repository",
    connectRepositoryHint: "Il collegamento OAuth si aprira dopo aver configurato i secret provider in Cloudflare.",
    english: "Inglese",
    italian: "Italiano",
    createAuditor: "Crea un nuovo profilo auditor",
    duplicateAuditor: "Duplica il profilo auditor corrente",
    deleteAuditor: "Elimina il profilo auditor corrente",
    saveAuditor: "Salva il profilo auditor",
    auditorProfiles: "Profili auditor salvati",
    openAuditor: "Apri profilo auditor",
    noName: "Nome non impostato",
    firstName: "Nome",
    lastName: "Cognome",
    phone: "Telefono",
    email: "Email",
    commands: "Comandi",
    reloadApp: "Ricarica app",
    select: "Seleziona",
    evidenceInline: "Evidenze inline",
    photoTitle: "Aggiungi foto come evidenza",
    audioTitle: "Registra o segna audio come evidenza",
    documentTitle: "Allega documento come evidenza",
    geoTitle: "Acquisisci geolocalizzazione",
    signatureTitle: "Firma questa voce",
    geoUnavailable: "Geolocalizzazione non disponibile",
    geoDenied: "Geolocalizzazione non autorizzata",
    signed: "Firmato",
    addFile: "Aggiungi file o scatta evidenza",
    tapSignature: "Tocca per acquisire firma",
    completion: "Completamento",
    metaAssignment: "Commessa",
    metaAreaAuditor: "Auditor area",
    status: "Stato",
    risk: "Rischio",
    savedMeta: "Salvato",
    note: "Note",
    invalidAssignmentPeriod: "Periodo commessa non valido: la data fine ispezione e precedente alla data inizio.",
    outsidePeriod: "Data fuori periodo commessa",
    mustBeBetween: "deve stare tra",
    newAssignmentReady: "Nuova commessa audit pronta.",
    assignmentDuplicated: "Commessa audit duplicata.",
    assignmentDeleted: "Commessa audit eliminata.",
    assignmentSaved: "Commessa audit salvata. I dati scendono nella console e nelle Aree di ispezione.",
    newAuditorReady: "Nuovo profilo auditor pronto.",
    auditorDuplicated: "Profilo auditor duplicato.",
    auditorDeleted: "Profilo auditor eliminato.",
    auditorSaved: "Settings auditor salvati.",
    openBeforeSave: "Apri o crea una Area di ispezione prima di salvare il questionario.",
    requiredMissing: "Campi obbligatori mancanti",
    questionnaireSaved: "Questionario validato e salvato."
  }
} as const;

type UiText = Record<keyof (typeof UI)["en"], string>;
type CloudStateSnapshot = {
  appSettings: AppSettings;
  auditorProfiles: AuditorProfile[];
  assignments: AuditAssignment[];
  audits: AuditRecord[];
};

const VALUE_LABELS: Record<string, Record<AppLanguage, string>> = {
  Low: { en: "Low", it: "Basso" },
  Medium: { en: "Medium", it: "Medio" },
  High: { en: "High", it: "Alto" },
  Critical: { en: "Critical", it: "Critico" },
  Draft: { en: "Draft", it: "Bozza" },
  "In progress": { en: "In progress", it: "In corso" },
  "Ready for review": { en: "Ready for review", it: "Pronto per revisione" },
  Closed: { en: "Closed", it: "Chiuso" },
  Completed: { en: "Completed", it: "Completata" },
  "Not connected": { en: "Not connected", it: "Non collegato" },
  "Needs configuration": { en: "Needs configuration", it: "Configurazione richiesta" },
  Connected: { en: "Connected", it: "Collegato" },
  Conforme: { en: "Compliant", it: "Conforme" },
  Osservazione: { en: "Observation", it: "Osservazione" },
  "Non conformita minore": { en: "Minor non-conformity", it: "Non conformita minore" },
  "Non conformita maggiore": { en: "Major non-conformity", it: "Non conformita maggiore" },
  "Non applicabile": { en: "Not applicable", it: "Non applicabile" },
  "Health / safety": { en: "Health / safety", it: "Salute / sicurezza" },
  Environment: { en: "Environment", it: "Ambiente" },
  Security: { en: "Security", it: "Security" },
  "Community / Brand": { en: "Community / Brand", it: "Comunita / brand" },
  "Quality / ISO": { en: "Quality / ISO", it: "Qualita / ISO" },
  Environmental: { en: "Environmental", it: "Ambientale" },
  Medical: { en: "Medical", it: "Medicale" },
  "People / Fatigue": { en: "People / Fatigue", it: "Persone / Fatigue" },
  "IT & Cyber": { en: "IT & Cyber", it: "IT & Cyber" },
  "Negligible risk <5": { en: "Negligible risk <5", it: "Rischio trascurabile <5" },
  "Risk to follow [5,15]": { en: "Risk to follow [5,15]", it: "Rischio da monitorare [5,15]" },
  "Risk to treat >15": { en: "Risk to treat >15", it: "Rischio da trattare >15" }
};

function displayValue(value: unknown, language: AppLanguage) {
  const key = String(value ?? "");
  return VALUE_LABELS[key]?.[language] ?? key;
}

function riskFromCriticality(ci: number): { level: string; risk: RiskLevel } {
  if (ci < 5) return { level: "Negligible risk <5", risk: "Low" };
  if (ci <= 15) return { level: "Risk to follow [5,15]", risk: ci >= 12 ? "High" : "Medium" };
  return { level: "Risk to treat >15", risk: "Critical" };
}

function selectNeedsNarrative(value: unknown) {
  return ["No", "N/A", "Osservazione", "Non conformita minore", "Non conformita maggiore", "Non applicabile"].includes(String(value));
}

function auditorDisplayName(profile: AuditorProfile) {
  const fullName = [profile.firstName, profile.lastName].filter(Boolean).join(" ");
  if (fullName && profile.label && profile.label !== "Auditor") return `${fullName} (${profile.label})`;
  return fullName || profile.label || "Auditor";
}

function buildInheritedValues(assignment: AuditAssignment, auditor: AuditorProfile): AuditRecord["values"] {
  const auditorName = auditorDisplayName(auditor);
  const auditPeriod = [assignment.startDate, assignment.endDate].filter(Boolean).join(" / ");
  return {
    assignment_label: assignment.label,
    client: assignment.client,
    country: assignment.country,
    port_terminal: assignment.portTerminal,
    vessel: assignment.vessel,
    scope: assignment.scope,
    inspection_start: assignment.startDate,
    inspection_end: assignment.endDate,
    auditor_profile_id: auditor.id,
    report_date: assignment.startDate,
    audit_period: auditPeriod,
    place: [assignment.country, assignment.portTerminal].filter(Boolean).join(" - "),
    auditor: auditorName,
    audit_scope: assignment.scope,
    port_name: assignment.portTerminal,
    local_agency: assignment.client,
    address: assignment.portTerminal
  };
}

function mergeInheritedValues(values: AuditRecord["values"], inherited: AuditRecord["values"]) {
  const explicit = Object.fromEntries(Object.entries(values).filter(([key, value]) => !inheritedFieldIds.has(key) && value !== "" && value !== undefined));
  return { ...inherited, ...explicit };
}

function isInheritedField(fieldId: string) {
  return inheritedFieldIds.has(fieldId);
}

function parseDate(value: unknown) {
  if (!value || typeof value !== "string") return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateTime(value: string | undefined, language: AppLanguage) {
  return value ? new Date(value).toLocaleString(language === "it" ? "it-IT" : "en-GB") : "";
}

function dateValidationMessage(label: string, value: unknown, assignment: AuditAssignment, ui: UiText = UI.en) {
  const date = parseDate(value);
  const start = parseDate(assignment.startDate);
  const end = parseDate(assignment.endDate);
  if (!date || !start || !end) return "";
  if (end < start) return ui.invalidAssignmentPeriod;
  if (date < start || date > end) return `${ui.outsidePeriod}: ${label} ${ui.mustBeBetween} ${assignment.startDate} e ${assignment.endDate}.`;
  return "";
}

type NormDialogData = {
  field?: AuditField;
  reference?: NormReference;
};

type LinkedField = {
  templateId: string;
  sectionId: string;
  fieldId: string;
  templateTitle: string;
  sectionTitle: string;
  fieldLabel: string;
};

function normForField(field: AuditField) {
  return findNormReference(field.normRef);
}

function hasNormData(field: AuditField) {
  return Boolean(field.normRef || field.guidance || field.sourceUrl);
}

function normTitleForField(field: AuditField) {
  return normForField(field)?.title || field.normRef || field.label;
}

function normUrlForField(field: AuditField) {
  return field.sourceUrl || normForField(field)?.url;
}

function fieldMatchesNorm(field: AuditField, reference: NormReference) {
  const fieldReference = normForField(field);
  const fieldCompact = String(field.normRef ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const referenceCompact = reference.code.toLowerCase().replace(/[^a-z0-9]/g, "");
  return fieldReference?.id === reference.id || Boolean(fieldCompact && (fieldCompact.includes(referenceCompact) || referenceCompact.includes(fieldCompact)));
}

function FieldEvidenceActions({
  field,
  onAction,
  onOpenNorm,
  ui,
  language
}: {
  field: AuditField;
  onAction: (fieldId: string, action: string, value: string | number | boolean) => void;
  onOpenNorm: (field: AuditField) => void;
  ui: UiText;
  language: AppLanguage;
}) {
  const photoRef = useRef<HTMLInputElement>(null);
  const docRef = useRef<HTMLInputElement>(null);

  const mark = (action: string, value?: string) => onAction(`${field.id}__${action}`, action, value ?? new Date().toLocaleString(language === "it" ? "it-IT" : "en-GB"));
  const geo = () => {
    if (!navigator.geolocation) {
      mark("geo", ui.geoUnavailable);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => mark("geo", `${position.coords.latitude.toFixed(6)}, ${position.coords.longitude.toFixed(6)}`),
      () => mark("geo", ui.geoDenied)
    );
  };

  return (
    <div className="field-actions" aria-label={ui.evidenceInline}>
      <button type="button" title={ui.photoTitle} aria-label={ui.photoTitle} onClick={() => photoRef.current?.click()}>
        <Camera size={14} />
      </button>
      <input ref={photoRef} type="file" accept="image/*" capture="environment" onChange={(event) => mark("photo", event.target.files?.[0]?.name)} />
      <button type="button" title={ui.audioTitle} aria-label={ui.audioTitle} onClick={() => mark("audio")}>
        <Mic size={14} />
      </button>
      <button type="button" title={ui.documentTitle} aria-label={ui.documentTitle} onClick={() => docRef.current?.click()}>
        <Archive size={14} />
      </button>
      <input ref={docRef} type="file" accept=".pdf,.doc,.docx,image/*,audio/*,video/*" onChange={(event) => mark("document", event.target.files?.[0]?.name)} />
      <button type="button" title={ui.geoTitle} aria-label={ui.geoTitle} onClick={geo}>
        <Globe2 size={14} />
      </button>
      <button type="button" title={ui.signatureTitle} aria-label={ui.signatureTitle} onClick={() => mark("signature", `${ui.signed} ${new Date().toLocaleString(language === "it" ? "it-IT" : "en-GB")}`)}>
        <Fingerprint size={14} />
      </button>
      {hasNormData(field) && (
        <button type="button" className="norm-action" title={`${ui.normTitle}: ${normTitleForField(field)}`} aria-label={`${ui.normTitle}: ${normTitleForField(field)}`} onClick={() => onOpenNorm(field)}>
          <BookOpen size={14} />
          <span>{ui.normButton}</span>
        </button>
      )}
    </div>
  );
}

function FieldControl({ field, value, onChange, ui, language }: { field: AuditField; value: string | number | boolean | undefined; onChange: (value: string | number | boolean) => void; ui: UiText; language: AppLanguage }) {
  const common = {
    id: field.id,
    required: field.required,
    value: typeof value === "boolean" ? String(value) : value ?? "",
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => onChange(event.target.value)
  };

  if (field.kind === "textarea") return <textarea {...common} rows={2} />;
  if (field.kind === "select") {
    return (
      <select {...common}>
        <option value="">{ui.select}</option>
        {field.options?.map((option) => (
          <option key={option} value={option}>
            {displayValue(option, language)}
          </option>
        ))}
      </select>
    );
  }
  if (field.kind === "rating") {
    return (
      <div className="rating" role="radiogroup" aria-label={field.label}>
        {[1, 2, 3, 4, 5].map((score) => (
          <button type="button" className={Number(value) === score ? "active" : ""} onClick={() => onChange(score)} key={score}>
            {score}
          </button>
        ))}
      </div>
    );
  }
  if (field.kind === "checkbox") {
    return <input type="checkbox" checked={Boolean(value)} onChange={(event) => onChange(event.target.checked)} />;
  }
  if (field.kind === "media") {
    return (
      <label className="upload">
        <UploadCloud size={18} />
        <span>{value ? String(value) : ui.addFile}</span>
        <input type="file" accept="image/*,audio/*,video/*,.pdf,.doc,.docx" capture="environment" onChange={(event) => onChange(event.target.files?.[0]?.name ?? "")} />
      </label>
    );
  }
  if (field.kind === "signature") {
    return (
      <button type="button" className="signature" onClick={() => onChange(`${ui.signed} ${new Date().toLocaleString(language === "it" ? "it-IT" : "en-GB")}`)}>
        <PenLine size={18} />
        {value ? String(value) : ui.tapSignature}
      </button>
    );
  }
  return <input {...common} type={field.kind === "number" ? "number" : field.kind === "date" ? "date" : "text"} />;
}

function Completion({ audit, template, values, ui }: { audit: AuditRecord; template: AuditTemplate; values?: AuditRecord["values"]; ui: UiText }) {
  const fields = template.sections.flatMap((section) => section.fields);
  const source = values ?? audit.values;
  const done = fields.filter((field) => source[field.id] !== undefined && source[field.id] !== "").length;
  const pct = Math.round((done / Math.max(fields.length, 1)) * 100);
  return (
    <div className="meter" aria-label={`${ui.completion} ${pct}%`}>
      <span style={{ width: `${pct}%` }} />
      <strong>{pct}%</strong>
    </div>
  );
}

function renderReportText(audit: AuditRecord, template: AuditTemplate, auditor: AuditorProfile, assignment: AuditAssignment | undefined, ui: UiText) {
  const auditorName = [auditor.firstName, auditor.lastName].filter(Boolean).join(" ");
  const lines = [
    `${template.title}`,
    `${ui.metaAssignment}: ${assignment?.label || "-"}`,
    `${ui.client}: ${assignment?.client || "-"}`,
    `Country: ${assignment?.country || "-"}`,
    `Name of Port / Terminal: ${assignment?.portTerminal || audit.site || "-"}`,
    `Vessel: ${assignment?.vessel || "-"}`,
    `Scope: ${assignment?.scope || "-"}`,
    `Record: ${audit.title}`,
    `Auditor: ${auditorName || "-"}`,
    `Auditor email: ${auditor.email || "-"}`,
    `Auditor phone: ${auditor.phone || "-"}`,
    `Inspection start: ${assignment?.startDate || "-"}`,
    `Inspection end: ${assignment?.endDate || "-"}`,
    `Site: ${assignment?.portTerminal || audit.site || "-"}`,
    `Status: ${audit.status}`,
    `Risk: ${audit.risk}`,
    `Saved: ${audit.savedAt || "-"}`,
    ""
  ];
  template.sections.forEach((section) => {
    lines.push(section.title);
    section.fields.forEach((field) => {
      lines.push(`${field.label}: ${audit.values[field.id] ?? ""}`);
      if (audit.values[`${field.id}__narrative`]) lines.push(`${ui.note}: ${audit.values[`${field.id}__narrative`]}`);
      if ((audit.values[field.id] || audit.values[`${field.id}__narrative`]) && hasNormData(field)) {
        const reference = normForField(field);
        lines.push(`${ui.normReference}: ${field.normRef || reference?.code || "-"}${reference?.title ? ` - ${reference.title}` : ""}`);
        if (field.guidance) lines.push(`${ui.guidance}: ${field.guidance}`);
        const url = normUrlForField(field);
        if (url) lines.push(`${ui.sourceUrl}: ${url}`);
      }
    });
    lines.push("");
  });
  return lines.join("\n");
}

function answerTone(value: unknown) {
  const normalized = String(value ?? "").toLowerCase();
  if (!normalized) return "answer-empty";
  if (normalized.includes("non conformita maggiore") || normalized.includes("critical") || normalized.includes("risk to treat")) return "answer-danger";
  if (normalized.includes("non conformita minore") || normalized.includes("high") || normalized.includes("risk to follow")) return "answer-warning";
  if (normalized.includes("osservazione") || normalized.includes("medium")) return "answer-note";
  if (normalized.includes("conforme") || normalized.includes("low") || normalized.includes("negligible")) return "answer-ok";
  return "answer-neutral";
}

function ReportPreview({ audit, template, auditor, assignment, ui, language }: { audit: AuditRecord; template: AuditTemplate; auditor: AuditorProfile; assignment: AuditAssignment; ui: UiText; language: AppLanguage }) {
  const auditorName = [auditor.firstName, auditor.lastName].filter(Boolean).join(" ");
  const meta = [
    [ui.metaAssignment, assignment.label || "-"],
    [ui.client, assignment.client || "-"],
    ["Country", assignment.country || "-"],
    ["Port / Terminal", assignment.portTerminal || audit.site || "-"],
    ["Vessel", assignment.vessel || "-"],
    ["Record", audit.title],
    [ui.metaAreaAuditor, auditorName || "-"],
    ["Email", auditor.email || "-"],
    [ui.phone, auditor.phone || "-"],
    [ui.startDate, assignment.startDate || "-"],
    [ui.endDate, assignment.endDate || "-"],
    [ui.status, displayValue(audit.status, language)],
    [ui.risk, displayValue(audit.risk, language)],
    [ui.savedMeta, formatDateTime(audit.savedAt, language) || "-"]
  ];

  return (
    <article className="report-preview">
      <section className="preview-meta">
        {meta.map(([label, value]) => (
          <div key={label}>
            <span>{label}</span>
            <strong className={label === ui.risk ? answerTone(value) : ""}>{value}</strong>
          </div>
        ))}
      </section>
      {template.sections.map((section) => (
        <section className="preview-section" key={section.id}>
          <h3>{section.title}</h3>
          <div className="preview-rows">
            {section.fields.map((field) => {
              const value = audit.values[field.id];
              const note = audit.values[`${field.id}__narrative`];
              return (
                <div className="preview-row" key={field.id}>
                  <span>{field.label}</span>
                  <div>
                    <strong className={`answer-tag ${answerTone(value)}`}>{value ? displayValue(value, language) : "-"}</strong>
                    {note && <p className="preview-note">{ui.note}: {String(note)}</p>}
                    {hasNormData(field) && (
                      <div className="preview-norm">
                        <BookOpen size={14} />
                        <span>{ui.normReference}: <strong>{field.normRef || normForField(field)?.code}</strong></span>
                        {normUrlForField(field) && (
                          <a href={normUrlForField(field)} target="_blank" rel="noreferrer" title={ui.sourceUrl}>
                            <ExternalLink size={13} />
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </article>
  );
}

export function App() {
  const [appSettings, setAppSettings] = useState(() => loadAppSettings());
  const language = appSettings.language;
  const ui = UI[language];
  const [activeRepositoryId, setActiveRepositoryId] = useState(appSettings.primaryRepositoryId || appSettings.repositories[0]?.id || "");
  const [auditorProfiles, setAuditorProfiles] = useState<AuditorProfile[]>(() => loadAuditorProfiles());
  const [activeAuditorId, setActiveAuditorId] = useState<string>("");
  const [assignments, setAssignments] = useState<AuditAssignment[]>(() => loadAssignments(loadAuditorProfiles()[0]?.id ?? ""));
  const [activeAssignmentId, setActiveAssignmentId] = useState<string>("");
  const activeAssignment = assignments.find((assignment) => assignment.id === activeAssignmentId) ?? assignments[0] ?? newAssignment(auditorProfiles[0]?.id ?? "");
  const assignmentAuditorIds = activeAssignment.auditorProfileIds.length ? activeAssignment.auditorProfileIds : [activeAssignment.auditorProfileId].filter(Boolean);
  const auditor = auditorProfiles.find((profile) => profile.id === (activeAuditorId || assignmentAuditorIds[0])) ?? auditorProfiles[0] ?? newAuditorProfile();
  const activeRepository = appSettings.repositories.find((repository) => repository.id === activeRepositoryId) ?? appSettings.repositories[0] ?? newExternalRepository("google-drive");

  const [audits, setAudits] = useState<AuditRecord[]>(() => {
    const stored = loadAudits();
    const defaultAssignment = loadAssignments(loadAuditorProfiles()[0]?.id ?? "")[0];
    const normalized = stored.map((audit) =>
      ["iso-integrated-internal-audit", "iso-reference-table", "port-agent-full-style"].includes(audit.templateId)
        ? { ...audit, assignmentId: audit.assignmentId || defaultAssignment?.id, templateId: "iso-questionnaire", title: "ISO Questionnaire" }
        : { ...audit, assignmentId: audit.assignmentId || defaultAssignment?.id }
    );
    return normalized.length ? normalized : [newAudit(templates[0], defaultAssignment)];
  });
  const [view, setView] = useState<View>("assignment");
  const [activeId, setActiveId] = useState(audits[0]?.id);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [normDialog, setNormDialog] = useState<NormDialogData | null>(null);
  const [normSearch, setNormSearch] = useState("");
  const [normCategory, setNormCategory] = useState("ALL");
  const [highlightedFieldId, setHighlightedFieldId] = useState<string | null>(null);
  const [validationMessage, setValidationMessage] = useState("");
  const [inspectionOpen, setInspectionOpen] = useState(false);
  const [cloudStatus, setCloudStatus] = useState("Cloud status: checking");
  const [cloudUser, setCloudUser] = useState("");

  const saveCloudSnapshot = (patch: Partial<CloudStateSnapshot> = {}) => {
    const snapshot: CloudStateSnapshot = {
      appSettings: patch.appSettings ?? appSettings,
      auditorProfiles: patch.auditorProfiles ?? auditorProfiles,
      assignments: patch.assignments ?? assignments,
      audits: patch.audits ?? audits
    };
    if (snapshot.appSettings.cloudMode !== "cloud-online") return;
    fetch("/api/state", {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(snapshot)
    })
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        setCloudStatus("Cloud status: saved online");
      })
      .catch(() => setCloudStatus("Cloud status: login required or API not available"));
  };

  useEffect(() => {
    let cancelled = false;
    fetch("/api/auth/me")
      .then((response) => (response.ok ? (response.json() as Promise<{ authenticated?: boolean; user?: { email?: string } }>) : null))
      .then((session) => {
        if (cancelled) return;
        if (!session?.authenticated) {
          setCloudStatus("Cloud status: Cloudflare Access login required");
          return;
        }
        setCloudUser(session.user?.email || "");
        setCloudStatus("Cloud status: connected");
        return fetch("/api/state")
          .then((response) => (response.ok ? (response.json() as Promise<{ state?: Partial<CloudStateSnapshot> }>) : null))
          .then((payload) => {
            if (cancelled || !payload?.state) return;
            const state = payload.state as Partial<CloudStateSnapshot>;
            if (state.appSettings) {
              setAppSettings(state.appSettings);
              saveAppSettings(state.appSettings);
              setActiveRepositoryId(state.appSettings.primaryRepositoryId || state.appSettings.repositories?.[0]?.id || "");
            }
            if (state.auditorProfiles?.length) {
              setAuditorProfiles(state.auditorProfiles);
              saveAuditorProfiles(state.auditorProfiles);
            }
            if (state.assignments?.length) {
              setAssignments(state.assignments);
              saveAssignments(state.assignments);
            }
            if (state.audits?.length) {
              setAudits(state.audits);
              saveAudits(state.audits);
              setActiveId(state.audits[0]?.id);
            }
            setCloudStatus("Cloud status: online data loaded");
          });
      })
      .catch(() => setCloudStatus("Cloud status: API not available on this host"));
    return () => {
      cancelled = true;
    };
  }, []);

  const persistSettings = (next: AppSettings) => {
    setAppSettings(next);
    saveAppSettings(next);
    saveCloudSnapshot({ appSettings: next });
  };

  const updateLanguage = (nextLanguage: AppLanguage) => {
    persistSettings({ ...appSettings, language: nextLanguage });
  };

  const assignmentAudits = audits.filter((audit) => audit.assignmentId === activeAssignment.id);
  const activeAudit = assignmentAudits.find((audit) => audit.id === activeId) ?? assignmentAudits[0];
  const activeTemplate = templates.find((template) => template.id === activeAudit?.templateId) ?? templates[0];
  const activeTemplateRecords = audits.filter((audit) => audit.templateId === activeTemplate.id && audit.assignmentId === activeAssignment.id);
  const assignmentAuditors = auditorProfiles.filter((profile) => assignmentAuditorIds.includes(profile.id));
  const assignmentAuditor = assignmentAuditors[0] ?? auditor;
  const activeAreaAuditorId = activeAssignment.areaAuditorMap[activeTemplate.id] || assignmentAuditor.id;
  const activeAreaAuditor = auditorProfiles.find((profile) => profile.id === activeAreaAuditorId) ?? assignmentAuditor;
  const inheritedValues = buildInheritedValues(activeAssignment, activeAreaAuditor);
  const effectiveValues = activeAudit ? mergeInheritedValues(activeAudit.values, inheritedValues) : inheritedValues;
  const inheritedValuesForTemplate = (template: AuditTemplate) => {
    const areaAuditorId = activeAssignment.areaAuditorMap[template.id] || assignmentAuditor.id;
    const areaAuditor = auditorProfiles.find((profile) => profile.id === areaAuditorId) ?? assignmentAuditor;
    return buildInheritedValues(activeAssignment, areaAuditor);
  };

  const persist = (next: AuditRecord[]) => {
    setAudits(next);
    saveAudits(next);
    saveCloudSnapshot({ audits: next });
  };

  const persistAuditors = (next: AuditorProfile[]) => {
    setAuditorProfiles(next);
    saveAuditorProfiles(next);
    saveCloudSnapshot({ auditorProfiles: next });
  };

  const persistAssignments = (next: AuditAssignment[]) => {
    setAssignments(next);
    saveAssignments(next);
    saveCloudSnapshot({ assignments: next });
  };

  const updateAssignment = (patch: Partial<AuditAssignment>) => {
    const nextAssignment = { ...activeAssignment, ...patch, updatedAt: new Date().toISOString() };
    if (patch.auditorProfileIds) {
      nextAssignment.auditorProfileId = patch.auditorProfileIds[0] || "";
      nextAssignment.areaAuditorMap = Object.fromEntries(Object.entries(nextAssignment.areaAuditorMap).filter(([, auditorId]) => patch.auditorProfileIds?.includes(auditorId)));
    }
    persistAssignments(assignments.map((assignment) => (assignment.id === activeAssignment.id ? nextAssignment : assignment)));
    if (patch.portTerminal !== undefined || patch.country !== undefined || patch.client !== undefined || patch.scope !== undefined || patch.startDate !== undefined || patch.endDate !== undefined || patch.auditorProfileIds !== undefined) {
      persist(
        audits.map((audit) => {
          if (audit.assignmentId !== activeAssignment.id) return audit;
          const template = templates.find((item) => item.id === audit.templateId) ?? activeTemplate;
          const nextAuditorId = nextAssignment.areaAuditorMap[template.id] || nextAssignment.auditorProfileIds[0] || "";
          const nextAuditor = auditorProfiles.find((profile) => profile.id === nextAuditorId) ?? auditor;
          const nextInherited = buildInheritedValues(nextAssignment, nextAuditor);
          return {
            ...audit,
            site: nextAssignment.portTerminal || audit.site,
            values: mergeInheritedValues(audit.values, nextInherited),
            updatedAt: new Date().toISOString()
          };
        })
      );
    }
  };

  const toggleAssignmentAuditor = (profileId: string) => {
    const selected = assignmentAuditorIds.includes(profileId) ? assignmentAuditorIds.filter((id) => id !== profileId) : [...assignmentAuditorIds, profileId];
    updateAssignment({ auditorProfileIds: selected.length ? selected : [profileId] });
  };

  const assignAreaAuditor = (templateId: string, profileId: string) => {
    const profile = auditorProfiles.find((item) => item.id === profileId);
    updateAssignment({ areaAuditorMap: { ...activeAssignment.areaAuditorMap, [templateId]: profileId } });
    persist(
      audits.map((audit) =>
        audit.assignmentId === activeAssignment.id && audit.templateId === templateId
          ? { ...audit, values: { ...audit.values, auditor_profile_id: profileId, auditor: profile ? auditorDisplayName(profile) : "" }, updatedAt: new Date().toISOString() }
          : audit
      )
    );
  };

  const createAssignment = () => {
    const created = newAssignment(auditor.id);
    persistAssignments([created, ...assignments]);
    setActiveAssignmentId(created.id);
    setActiveId("");
    setView("assignment");
    setValidationMessage(ui.newAssignmentReady);
  };

  const duplicateAssignment = () => {
    const copy: AuditAssignment = { ...activeAssignment, id: crypto.randomUUID(), label: `${activeAssignment.label} copy`, status: "Draft", savedAt: undefined, updatedAt: new Date().toISOString() };
    persistAssignments([copy, ...assignments]);
    setActiveAssignmentId(copy.id);
    setActiveId("");
    setView("assignment");
    setValidationMessage(ui.assignmentDuplicated);
  };

  const deleteAssignment = () => {
    const remaining = assignments.filter((assignment) => assignment.id !== activeAssignment.id);
    const next = remaining.length ? remaining : [newAssignment(auditor.id)];
    persistAssignments(next);
    persist(audits.filter((audit) => audit.assignmentId !== activeAssignment.id));
    setActiveAssignmentId(next[0].id);
    setActiveId("");
    setView("assignment");
    setValidationMessage(ui.assignmentDeleted);
  };

  const saveAssignment = () => {
    const dateIssue = dateValidationMessage(ui.endDate, activeAssignment.endDate, activeAssignment, ui);
    if (dateIssue) {
      setValidationMessage(dateIssue);
      return;
    }
    const savedAt = new Date().toISOString();
    persistAssignments(assignments.map((assignment) => (assignment.id === activeAssignment.id ? { ...assignment, savedAt, updatedAt: savedAt } : assignment)));
    setValidationMessage(ui.assignmentSaved);
  };

  const updateAuditor = (patch: Partial<AuditorProfile>) => {
    const next = auditorProfiles.map((profile) => (profile.id === auditor.id ? { ...profile, ...patch } : profile));
    persistAuditors(next.length ? next : [{ ...auditor, ...patch }]);
  };

  const createAuditor = () => {
    const created = newAuditorProfile();
    persistAuditors([created, ...auditorProfiles]);
    setActiveAuditorId(created.id);
    setValidationMessage(ui.newAuditorReady);
  };

  const duplicateAuditor = () => {
    const copy: AuditorProfile = { ...auditor, id: crypto.randomUUID(), label: `${auditor.label || "Auditor"} copy`, savedAt: undefined };
    persistAuditors([copy, ...auditorProfiles]);
    setActiveAuditorId(copy.id);
    setValidationMessage(ui.auditorDuplicated);
  };

  const deleteAuditor = () => {
    const remaining = auditorProfiles.filter((profile) => profile.id !== auditor.id);
    const next = remaining.length ? remaining : [newAuditorProfile()];
    persistAuditors(next);
    setActiveAuditorId(next[0].id);
    setValidationMessage(ui.auditorDeleted);
  };

  const saveAuditorSettings = () => {
    const savedAt = new Date().toISOString();
    const next = auditorProfiles.map((profile) => (profile.id === auditor.id ? { ...profile, savedAt } : profile));
    persistAuditors(next.length ? next : [{ ...auditor, savedAt }]);
    setValidationMessage(ui.auditorSaved);
  };

  const updateOnlineSettings = (patch: Partial<AppSettings>) => {
    persistSettings({ ...appSettings, ...patch });
    setValidationMessage(ui.repositoryReady);
  };

  const updateRepository = (patch: Partial<ExternalRepository>) => {
    const nextRepository = { ...activeRepository, ...patch };
    const repositories = appSettings.repositories.map((repository) => (repository.id === activeRepository.id ? nextRepository : repository));
    persistSettings({ ...appSettings, repositories });
  };

  const createRepository = () => {
    const created = newExternalRepository("google-drive");
    persistSettings({
      ...appSettings,
      repositories: [created, ...appSettings.repositories],
      primaryRepositoryId: appSettings.primaryRepositoryId || created.id
    });
    setActiveRepositoryId(created.id);
    setValidationMessage(ui.repositoryReady);
  };

  const duplicateRepository = () => {
    const copy: ExternalRepository = { ...activeRepository, id: crypto.randomUUID(), label: `${activeRepository.label} copy`, status: "Needs configuration", savedAt: undefined };
    persistSettings({ ...appSettings, repositories: [copy, ...appSettings.repositories] });
    setActiveRepositoryId(copy.id);
    setValidationMessage(ui.repositoryReady);
  };

  const deleteRepository = () => {
    const remaining = appSettings.repositories.filter((repository) => repository.id !== activeRepository.id);
    const fallback = remaining.length ? remaining : [newExternalRepository("google-drive")];
    persistSettings({
      ...appSettings,
      repositories: fallback,
      primaryRepositoryId: appSettings.primaryRepositoryId === activeRepository.id ? fallback[0].id : appSettings.primaryRepositoryId
    });
    setActiveRepositoryId(fallback[0].id);
    setValidationMessage(ui.repositorySaved);
  };

  const saveRepository = () => {
    const savedAt = new Date().toISOString();
    const repositories = appSettings.repositories.map((repository) => (repository.id === activeRepository.id ? { ...activeRepository, savedAt } : repository));
    persistSettings({ ...appSettings, repositories, primaryRepositoryId: appSettings.primaryRepositoryId || activeRepository.id });
    setValidationMessage(ui.repositorySaved);
  };

  const connectRepository = () => {
    const provider = activeRepository.provider;
    const url = `/api/repositories/${activeRepository.id}/oauth/start?provider=${encodeURIComponent(provider)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setValidationMessage(ui.connectRepositoryHint);
  };

  const updateAudit = (patch: Partial<AuditRecord>, valuesPatch?: AuditRecord["values"]) => {
    if (!activeAudit) return;
    persist(
      audits.map((audit) =>
        audit.id === activeAudit.id ? { ...audit, ...patch, values: valuesPatch ? { ...audit.values, ...valuesPatch } : patch.values ?? audit.values, updatedAt: new Date().toISOString() } : audit
      )
    );
  };

  const updateValue = (fieldId: string, value: string | number | boolean) => {
    if (!activeAudit) return;
    const valuesPatch: AuditRecord["values"] = { [fieldId]: value };
    let riskPatch: Partial<AuditRecord> = { status: activeAudit.status === "Draft" ? "In progress" : activeAudit.status };
    const field = activeTemplate.sections.flatMap((section) => section.fields).find((item) => item.id === fieldId);
    if (field?.kind === "date") {
      const message = dateValidationMessage(field.label, value, activeAssignment, ui);
      if (message) setValidationMessage(message);
      else if (validationMessage.startsWith(UI.it.outsidePeriod) || validationMessage.startsWith(UI.en.outsidePeriod) || validationMessage.startsWith(UI.it.invalidAssignmentPeriod) || validationMessage.startsWith(UI.en.invalidAssignmentPeriod)) setValidationMessage("");
    }

    if (fieldId === "severity_index" || fieldId === "probability_index") {
      const severity = Number(fieldId === "severity_index" ? value : activeAudit.values.severity_index);
      const probability = Number(fieldId === "probability_index" ? value : activeAudit.values.probability_index);
      if (severity > 0 && probability > 0) {
        const ci = severity * probability;
        const derived = riskFromCriticality(ci);
        valuesPatch.criticality_index = ci;
        valuesPatch.criticality_level = derived.level;
        riskPatch.risk = derived.risk;
      }
    }

    if (fieldId === "criticality_index") {
      const ci = Number(value);
      if (ci > 0) {
        const derived = riskFromCriticality(ci);
        valuesPatch.criticality_level = derived.level;
        riskPatch.risk = derived.risk;
      }
    }

    if (fieldId === "criticality_level" && typeof value === "string" && criticalityPresets[value]) {
      const preset = criticalityPresets[value];
      valuesPatch.severity_index = preset.severity;
      valuesPatch.probability_index = preset.probability;
      valuesPatch.criticality_index = preset.ci;
      riskPatch.risk = preset.risk;
    }

    updateAudit(riskPatch, valuesPatch);
  };

  const updateRisk = (risk: RiskLevel) => {
    if (!activeAudit) return;
    const preset = auditRiskPresets[risk];
    updateAudit({ risk, status: activeAudit.status === "Draft" ? "In progress" : activeAudit.status }, {
      severity_index: preset.severity,
      probability_index: preset.probability,
      criticality_index: preset.ci,
      criticality_level: preset.level
    });
  };

  const openTemplate = (template: AuditTemplate) => {
    const existing = audits.find((audit) => audit.templateId === template.id && audit.assignmentId === activeAssignment.id);
    if (existing) {
      setActiveId(existing.id);
    } else {
      const created = { ...newAudit(template, activeAssignment), values: inheritedValuesForTemplate(template) };
      persist([created, ...audits]);
      setActiveId(created.id);
    }
    setActiveSectionId(null);
    setView("forms");
  };

  const createRecord = (template: AuditTemplate) => {
    const created = { ...newAudit(template, activeAssignment), values: inheritedValuesForTemplate(template) };
    persist([created, ...audits]);
    setActiveId(created.id);
    setActiveSectionId(null);
    setView("forms");
  };

  const duplicateRecord = () => {
    if (!activeAudit) return;
    const copy: AuditRecord = {
      ...activeAudit,
      id: crypto.randomUUID(),
      assignmentId: activeAssignment.id,
    title: `${activeAudit.title} copy`,
      status: "Draft",
      updatedAt: new Date().toISOString(),
      values: { ...activeAudit.values }
    };
    persist([copy, ...audits]);
    setActiveId(copy.id);
  };

  const deleteRecord = () => {
    if (!activeAudit) return;
    const remaining = audits.filter((audit) => audit.id !== activeAudit.id);
    const replacement = remaining.length ? undefined : { ...newAudit(activeTemplate, activeAssignment), values: inheritedValuesForTemplate(activeTemplate) };
    const next = replacement ? [replacement] : remaining;
    const fallback = next.find((audit) => audit.templateId === activeTemplate.id) ?? next[0];
    persist(next);
    setActiveId(fallback?.id);
  };

  const validateAndSave = () => {
    if (!activeAudit) {
      setValidationMessage(ui.openBeforeSave);
      return false;
    }
    const assignmentDateIssue = dateValidationMessage(ui.endDate, activeAssignment.endDate, activeAssignment, ui);
    if (assignmentDateIssue) {
      setValidationMessage(assignmentDateIssue);
      updateAudit({ status: "Draft" });
      return false;
    }
    const dateIssues = activeTemplate.sections
      .flatMap((section) => section.fields)
      .filter((field) => field.kind === "date")
      .map((field) => dateValidationMessage(field.label, effectiveValues[field.id], activeAssignment, ui))
      .filter(Boolean);
    if (dateIssues.length) {
      setValidationMessage(dateIssues[0]);
      updateAudit({ status: "Draft" });
      return false;
    }
    const requiredMissing = activeTemplate.sections
      .flatMap((section) => section.fields)
      .filter((field) => field.required && (effectiveValues[field.id] === undefined || effectiveValues[field.id] === ""))
      .map((field) => field.label);
    if (requiredMissing.length) {
      setValidationMessage(`${ui.requiredMissing}: ${requiredMissing.slice(0, 5).join(", ")}${requiredMissing.length > 5 ? "..." : ""}`);
      updateAudit({ status: "Draft" });
      return false;
    }
    setValidationMessage(ui.questionnaireSaved);
    updateAudit({ status: "Ready for review", savedAt: new Date().toISOString() }, effectiveValues);
    return true;
  };

  const printQuestionnaire = () => {
    if (!activeAudit) return;
    setPreviewOpen(true);
    window.setTimeout(() => window.print(), 50);
  };

  const forwardQuestionnaire = () => {
    if (!activeAudit) return;
    const subject = encodeURIComponent(`${activeTemplate.title} - ${activeAudit.title}`);
    const body = encodeURIComponent(renderReportText({ ...activeAudit, values: effectiveValues }, activeTemplate, activeAreaAuditor, activeAssignment, ui).slice(0, 1800));
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const openRoadmap = (templateId: string, sectionId: string) => {
    const template = templates.find((item) => item.id === templateId) ?? templates[0];
    const existing = audits.find((audit) => audit.templateId === template.id && audit.assignmentId === activeAssignment.id);
    if (existing) {
      setActiveId(existing.id);
    } else {
      const created = { ...newAudit(template, activeAssignment), values: inheritedValuesForTemplate(template) };
      persist([created, ...audits]);
      setActiveId(created.id);
    }
    setActiveSectionId(sectionId);
    setView("forms");
  };

  const focusField = (fieldId: string) => {
    setHighlightedFieldId(fieldId);
    window.setTimeout(() => {
      const element = document.getElementById(fieldId);
      element?.scrollIntoView({ behavior: "smooth", block: "center" });
      if (element instanceof HTMLElement) element.focus({ preventScroll: true });
    }, 120);
    window.setTimeout(() => setHighlightedFieldId((current) => (current === fieldId ? null : current)), 3200);
  };

  const openLinkedField = (link: LinkedField) => {
    const template = templates.find((item) => item.id === link.templateId);
    if (!template) return;
    const existing = audits.find((audit) => audit.templateId === template.id && audit.assignmentId === activeAssignment.id);
    if (existing) {
      setActiveId(existing.id);
    } else {
      const created = { ...newAudit(template, activeAssignment), values: inheritedValuesForTemplate(template) };
      persist([created, ...audits]);
      setActiveId(created.id);
    }
    setActiveSectionId(link.sectionId);
    setView("forms");
    setNormDialog(null);
    focusField(link.fieldId);
  };

  const sectionsToRender = activeSectionId ? activeTemplate.sections.filter((section) => section.id === activeSectionId) : activeTemplate.sections;
  const normCategories = Array.from(new Set(normativeReferences.map((reference) => reference.category)));
  const filteredNorms = normativeReferences.filter((reference) => {
    const haystack = `${reference.category} ${reference.code} ${reference.title} ${reference.description} ${reference.level}`.toLowerCase();
    return (normCategory === "ALL" || reference.category === normCategory) && haystack.includes(normSearch.toLowerCase().trim());
  });
  const linkedFieldsByNorm = (reference: NormReference) =>
    templates
      .flatMap((template) =>
        template.sections.flatMap((section) =>
          section.fields
            .filter((field) => fieldMatchesNorm(field, reference))
            .map((field) => ({
              templateId: template.id,
              sectionId: section.id,
              fieldId: field.id,
              templateTitle: template.menuLabel ?? template.title,
              sectionTitle: section.title,
              fieldLabel: field.label
            }))
        )
      );
  const visibleCoreNav = import.meta.env.PROD ? coreNav.filter((item) => item.id !== "sync") : coreNav;

  return (
    <div className="shell">
      <aside className="sidebar">
        <img src="/windward_nexus_logo_apr2026.png" alt="Windward Nexus" />
        <nav>
          {visibleCoreNav.slice(0, 2).map((item) => {
            const Icon = item.icon;
            const label = "labelKey" in item ? ui[item.labelKey] : item.label;
            return (
              <button key={item.id} className={view === item.id ? "active" : ""} onClick={() => setView(item.id)} title={label}>
                <Icon size={20} />
                <span>{label}</span>
              </button>
            );
          })}
          <button type="button" className={`nav-toggle ${inspectionOpen ? "active" : ""}`} onClick={() => setInspectionOpen(!inspectionOpen)} title={ui.inspectionAreas}>
            <Menu size={20} />
            <span>{ui.inspectionAreas}</span>
            <ChevronDown size={16} />
          </button>
          {inspectionOpen && (
            <div className="nav-submenu">
              {inspectionTemplates.map((template) => (
                <button key={template.id} className={view === "forms" && activeTemplate.id === template.id ? "active" : ""} onClick={() => openTemplate(template)} title={template.title}>
                  <ClipboardCheck size={20} />
                  <span>{template.menuLabel ?? template.title}</span>
                </button>
              ))}
            </div>
          )}
          <div className="nav-group">{ui.operations}</div>
          {visibleCoreNav.slice(2).map((item) => {
            const Icon = item.icon;
            const label = "labelKey" in item ? ui[item.labelKey] : item.label;
            return (
              <button key={item.id} className={view === item.id ? "active" : ""} onClick={() => setView(item.id)} title={label}>
                <Icon size={20} />
                <span>{label}</span>
              </button>
            );
          })}
        </nav>
        {!import.meta.env.PROD && (
          <div className="offline-badge">
            <TabletSmartphone size={18} />
            {ui.offline}
          </div>
        )}
      </aside>

      <main>
        <header className="topbar">
          <div>
            <span className="eyebrow">{view === "assignment" ? ui.masterRecord : view === "dashboard" ? ui.auditConsole : view === "norms" ? ui.normativeReferences : ui.auditWorkspace}</span>
            <h1>{view === "assignment" ? ui.assignmentNav : view === "dashboard" ? ui.auditConsole : view === "norms" ? ui.normativeReferences : activeAudit?.title ?? ui.newAudit}</h1>
          </div>
          {view !== "assignment" && (
            <div className="top-actions">
              <div className="context-card">
                <span>{activeAssignment.country || ui.countryUnset}</span>
                <strong>{activeAssignment.portTerminal || ui.portUnset}</strong>
                <small>{view === "forms" ? `${ui.area}: ${auditorDisplayName(activeAreaAuditor)}` : `${ui.team}: ${assignmentAuditors.map(auditorDisplayName).join(", ") || ui.auditorUnset}`}</small>
              </div>
              <select value={activeAudit?.risk ?? "Medium"} onChange={(event) => updateRisk(event.target.value as RiskLevel)} title={ui.setRisk}>
                {(["Low", "Medium", "High", "Critical"] as RiskLevel[]).map((risk) => (
                  <option key={risk} value={risk}>{displayValue(risk, language)}</option>
                ))}
              </select>
              <select value={activeAudit?.status ?? "Draft"} onChange={(event) => updateAudit({ status: event.target.value as AuditRecord["status"] })} title={ui.setStatus}>
                {(["Draft", "In progress", "Ready for review", "Closed"] as AuditRecord["status"][]).map((status) => (
                  <option key={status} value={status}>{displayValue(status, language)}</option>
                ))}
              </select>
            </div>
          )}
        </header>

        {view !== "assignment" && (
          <section className="inheritance-strip">
            <strong>{ui.assignment}:</strong>
            <span>{activeAssignment.label || "-"}</span>
            <strong>{ui.client}:</strong>
            <span>{activeAssignment.client || "-"}</span>
            <strong>{ui.period}:</strong>
            <span>{activeAssignment.startDate || "-"} / {activeAssignment.endDate || "-"}</span>
            <button type="button" onClick={() => setView("assignment")} title={ui.editAssignment}>
              {ui.editAssignment}
            </button>
          </section>
        )}

        {view === "assignment" && (
          <section className="assignment-layout">
            <div className="assignment-panel">
              <div className="form-heading">
                <div>
                  <span className="eyebrow">{ui.whoWhatWhenWhere}</span>
                  <h2>{activeAssignment.label}</h2>
                  <p>{ui.parentContext}</p>
                </div>
                <button type="button" className="primary-link" onClick={() => setView("dashboard")} title={ui.goConsole}>
                  {ui.goConsole}
                </button>
              </div>
              <div className="crud-bar assignment-crud">
                <input value={activeAssignment.label} onChange={(event) => updateAssignment({ label: event.target.value })} aria-label={ui.assignmentName} />
                <button type="button" onClick={createAssignment} title={ui.createAssignment}>
                  <Plus size={18} />
                  {ui.newButton}
                </button>
                <button type="button" onClick={duplicateAssignment} title={ui.duplicateAssignment}>
                  <Copy size={18} />
                  {ui.duplicate}
                </button>
                <button type="button" onClick={deleteAssignment} title={ui.deleteAssignment}>
                  <Trash2 size={18} />
                  {ui.delete}
                </button>
                <button type="button" onClick={saveAssignment} title={ui.saveAssignment}>
                  <Save size={18} />
                  {ui.save}
                </button>
              </div>
              {validationMessage && <div className="validation-message">{validationMessage}</div>}
              <div className="record-list assignment-records" aria-label={ui.assignmentsList}>
                {assignments.map((assignment) => (
                  <button key={assignment.id} type="button" className={assignment.id === activeAssignment.id ? "active" : ""} onClick={() => setActiveAssignmentId(assignment.id)} title={`${ui.openAssignment} ${assignment.label}`}>
                    <strong>{assignment.label}</strong>
                    <span>{assignment.portTerminal || ui.portUnset}</span>
                    <small>{assignment.savedAt ? `${ui.saved} ${formatDateTime(assignment.savedAt, language)}` : displayValue(assignment.status, language)}</small>
                  </button>
                ))}
              </div>
              <div className="field-grid">
                <label>
                  <span>{ui.clientOrg}</span>
                  <input value={activeAssignment.client} onChange={(event) => updateAssignment({ client: event.target.value })} placeholder={ui.clientPlaceholder} />
                </label>
                <label>
                  <span>{ui.country}</span>
                  <input value={activeAssignment.country} onChange={(event) => updateAssignment({ country: event.target.value })} placeholder={ui.country} />
                </label>
                <label>
                  <span>{ui.portTerminal}</span>
                  <input value={activeAssignment.portTerminal} onChange={(event) => updateAssignment({ portTerminal: event.target.value })} placeholder={ui.portPlaceholder} />
                </label>
                <label>
                  <span>{ui.vesselAsset}</span>
                  <input value={activeAssignment.vessel} onChange={(event) => updateAssignment({ vessel: event.target.value })} placeholder={ui.optional} />
                </label>
                <label className="wide">
                  <span>{ui.auditorTeam}</span>
                  <div className="auditor-picker">
                    {auditorProfiles.map((profile) => (
                      <button
                        key={profile.id}
                        type="button"
                        className={assignmentAuditorIds.includes(profile.id) ? "selected" : ""}
                        onClick={() => toggleAssignmentAuditor(profile.id)}
                        title={`${ui.toggleAuditor}: ${auditorDisplayName(profile)}`}
                      >
                        <CheckCircle2 size={16} />
                        {auditorDisplayName(profile)}
                      </button>
                    ))}
                  </div>
                </label>
                <label>
                  <span>{ui.assignmentStatus}</span>
                  <select value={activeAssignment.status} onChange={(event) => updateAssignment({ status: event.target.value as AuditAssignment["status"] })}>
                    {(["Draft", "In progress", "Completed", "Closed"] as AuditAssignment["status"][]).map((status) => (
                      <option key={status} value={status}>{displayValue(status, language)}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>{ui.startDate}</span>
                  <input type="date" value={activeAssignment.startDate} onChange={(event) => updateAssignment({ startDate: event.target.value })} />
                  {dateValidationMessage(ui.startDate, activeAssignment.startDate, activeAssignment, ui) && <small className="field-error">{dateValidationMessage(ui.startDate, activeAssignment.startDate, activeAssignment, ui)}</small>}
                </label>
                <label>
                  <span>{ui.endDate}</span>
                  <input type="date" value={activeAssignment.endDate} onChange={(event) => updateAssignment({ endDate: event.target.value })} />
                  {dateValidationMessage(ui.endDate, activeAssignment.endDate, activeAssignment, ui) && <small className="field-error">{dateValidationMessage(ui.endDate, activeAssignment.endDate, activeAssignment, ui)}</small>}
                </label>
                <label className="wide">
                  <span>{ui.scope}</span>
                  <textarea rows={3} value={activeAssignment.scope} onChange={(event) => updateAssignment({ scope: event.target.value })} placeholder={ui.scopePlaceholder} />
                </label>
              </div>
            </div>
          </section>
        )}

        {view === "dashboard" && (
          <section className="grid dashboard">
            <div className="panel span-2">
              <div className="panel-title">
                <FolderKanban />
                <h2>{ui.roadmapTitle}</h2>
              </div>
              <ol className="roadmap">
                {roadmap.map((step, index) => (
                  <li key={step.label}>
                    <button type="button" onClick={() => openRoadmap(step.templateId, step.sectionId)}>
                      <span>{index + 1}</span>
                      {step.label}
                    </button>
                  </li>
                ))}
              </ol>
            </div>
            <div className="panel">
              <div className="panel-title">
                <ShieldCheck />
                <h2>{ui.standardCoverage}</h2>
              </div>
              <div className="chips">
                {["ISO 9001", "ISO 14001", "ISO 45001", "ISO 50001", "ISO 37001", "EcoVadis", "ISPS", "SOLAS", "D.Lgs. 81/2008", "D.Lgs. 272/1999", "UE 2019/883", "MARPOL"].map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>
            </div>
            <div className="panel span-3">
              <div className="panel-title">
                <Database />
                <h2>{ui.processes}</h2>
              </div>
              <div className="department-grid">
                {departments.map((dept) => (
                  <span key={dept}>{dept}</span>
                ))}
              </div>
            </div>
          </section>
        )}

        {view === "norms" && (
          <section className="norms-panel">
            <div className="form-heading">
              <div>
                <span className="eyebrow">{ui.normativeReferences}</span>
                <h2>{ui.normativeSubtitle}</h2>
              </div>
            </div>
            <div className="norm-toolbar">
              <input value={normSearch} onChange={(event) => setNormSearch(event.target.value)} placeholder={ui.searchNorms} aria-label={ui.searchNorms} />
              <select value={normCategory} onChange={(event) => setNormCategory(event.target.value)} aria-label={ui.allCategories}>
                <option value="ALL">{ui.allCategories}</option>
                {normCategories.map((category) => (
                  <option key={category} value={category}>
                    {category.replace("_", " / ")}
                  </option>
                ))}
              </select>
            </div>
            <div className="norm-grid">
              {filteredNorms.map((reference) => {
                const linkedFields = linkedFieldsByNorm(reference);
                return (
                  <article className="norm-card" key={reference.id}>
                    <div className="norm-card-head">
                      <span className="norm-category">{reference.category.replace("_", " / ")}</span>
                      <span>{reference.level}</span>
                    </div>
                    <h3>{reference.code}</h3>
                    <strong>{reference.title}</strong>
                    <p>{reference.description}</p>
                    <div className="norm-card-actions">
                      <button type="button" onClick={() => setNormDialog({ reference })} title={`${ui.normTitle}: ${reference.code}`}>
                        <BookOpen size={16} />
                        {ui.normButton}
                      </button>
                      <a href={reference.url} target="_blank" rel="noreferrer" title={ui.sourceUrl}>
                        <ExternalLink size={16} />
                        {ui.sourceUrl}
                      </a>
                    </div>
                    {linkedFields.length > 0 && (
                      <details>
                        <summary>{ui.linkedFields}: {linkedFields.length}</summary>
                        <ul className="linked-field-list">
                          {linkedFields.map((fieldLink) => (
                            <li key={`${fieldLink.templateId}-${fieldLink.sectionId}-${fieldLink.fieldId}`}>
                              <button type="button" onClick={() => openLinkedField(fieldLink)} title={`${ui.openLinkedField}: ${fieldLink.fieldLabel}`}>
                                <span>{fieldLink.templateTitle}</span>
                                <small>{fieldLink.sectionTitle}</small>
                                <strong>{fieldLink.fieldLabel}</strong>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </article>
                );
              })}
              {filteredNorms.length === 0 && <div className="empty-state">{ui.noNorms}</div>}
            </div>
          </section>
        )}

        {view === "forms" && activeAudit && (
          <section className="forms-layout">
            <div className="form-panel">
              <div className="form-heading">
                <div>
                  <span className="eyebrow">{activeTemplate.domain}</span>
                  <h2>{activeTemplate.title}</h2>
                  <p>{activeTemplate.standard.join(" / ")}</p>
                </div>
              <Completion audit={activeAudit} template={activeTemplate} values={effectiveValues} ui={ui} />
              </div>
              <div className="area-owner">
                <span>{ui.areaOwner}</span>
                <select value={activeAreaAuditor.id} onChange={(event) => assignAreaAuditor(activeTemplate.id, event.target.value)} title={ui.assignArea}>
                  {(assignmentAuditors.length ? assignmentAuditors : auditorProfiles).map((profile) => (
                    <option key={profile.id} value={profile.id}>
                      {auditorDisplayName(profile)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="crud-bar">
                <input value={activeAudit.title} onChange={(event) => updateAudit({ title: event.target.value })} aria-label={ui.recordTitle} />
                <button type="button" onClick={() => createRecord(activeTemplate)} title={ui.createRecord}>
                  <Plus size={18} />
                  {ui.newButton}
                </button>
                <button type="button" onClick={duplicateRecord} title={ui.duplicateRecord}>
                  <Copy size={18} />
                  {ui.duplicate}
                </button>
                <button type="button" onClick={deleteRecord} disabled={audits.length === 0} title={ui.deleteRecord}>
                  <Trash2 size={18} />
                  {ui.delete}
                </button>
                <button type="button" onClick={validateAndSave} title={ui.validateSave}>
                  <Save size={18} />
                  {ui.save}
                </button>
                <button type="button" onClick={() => setPreviewOpen(true)} title={ui.preview}>
                  <FileText size={18} />
                  {ui.preview}
                </button>
                <button type="button" onClick={printQuestionnaire} title={ui.print}>
                  <Printer size={18} />
                  {ui.print}
                </button>
                <button type="button" onClick={forwardQuestionnaire} title={ui.forward}>
                  <Send size={18} />
                  {ui.forward}
                </button>
                {activeSectionId && (
                  <button type="button" onClick={() => setActiveSectionId(null)} title={ui.allSections}>
                    <FileText size={18} />
                    {ui.allSections}
                  </button>
                )}
              </div>
              {validationMessage && <div className="validation-message">{validationMessage}</div>}
              <div className="record-list" aria-label={ui.selectedRecords}>
                {activeTemplateRecords.map((audit) => (
                  <button key={audit.id} className={audit.id === activeAudit.id ? "active" : ""} onClick={() => setActiveId(audit.id)}>
                    <strong>{audit.title}</strong>
                    <span>{audit.site || ui.siteUnset}</span>
                    <small>{audit.savedAt ? `${ui.savedMeta} ${formatDateTime(audit.savedAt, language)}` : displayValue(audit.status, language)}</small>
                  </button>
                ))}
              </div>
              {sectionsToRender.map((section) => (
                <fieldset key={section.id}>
                  <legend>
                    <FileText size={18} />
                    {section.title}
                    <small>{section.source}</small>
                  </legend>
                  <div className="field-grid">
                    {section.fields.map((field) => (
                      <label key={field.id} className={`${field.kind === "textarea" || field.kind === "media" || field.kind === "signature" ? "wide" : ""} ${highlightedFieldId === field.id ? "field-linked-highlight" : ""}`.trim()}>
                        <span className="field-head">
                          <span>
                            {field.label}
                            {field.required && <b>*</b>}
                          </span>
                          <FieldEvidenceActions field={field} ui={ui} language={language} onOpenNorm={(targetField) => setNormDialog({ field: targetField, reference: normForField(targetField) })} onAction={(targetFieldId, _action, value) => updateValue(targetFieldId, value)} />
                        </span>
                        {isInheritedField(field.id) && <small className="inherited-badge">{ui.inherited}</small>}
                        <FieldControl field={field} value={isInheritedField(field.id) ? inheritedValues[field.id] : effectiveValues[field.id]} ui={ui} language={language} onChange={(value) => updateValue(field.id, value)} />
                        {field.kind === "date" && dateValidationMessage(field.label, effectiveValues[field.id], activeAssignment, ui) && <small className="field-error">{dateValidationMessage(field.label, effectiveValues[field.id], activeAssignment, ui)}</small>}
                        {field.kind === "select" && selectNeedsNarrative(effectiveValues[field.id]) && (
                          <textarea
                            className="narrative-box"
                            rows={2}
                            value={String(activeAudit.values[`${field.id}__narrative`] ?? "")}
                            onChange={(event) => updateValue(`${field.id}__narrative`, event.target.value)}
                            placeholder={`${ui.narrativePlaceholder}: ${String(effectiveValues[field.id]).toLowerCase()}`}
                          />
                        )}
                      </label>
                    ))}
                  </div>
                </fieldset>
              ))}
              <div className="final-actions">
                <button type="button" onClick={validateAndSave} title={ui.validateSave}>
                  <Save size={18} />
                  {ui.validateSave}
                </button>
                <button type="button" onClick={() => setPreviewOpen(true)} title={ui.preview}>
                  <FileText size={18} />
                  {ui.preview}
                </button>
                <button type="button" onClick={printQuestionnaire} title={ui.print}>
                  <Printer size={18} />
                  {ui.print}
                </button>
                <button type="button" onClick={forwardQuestionnaire} title={ui.forward}>
                  <Send size={18} />
                  {ui.forward}
                </button>
              </div>
            </div>
          </section>
        )}

        {view === "forms" && !activeAudit && (
          <section className="empty-state">
            <h2>{ui.noInspectionOpen}</h2>
            <p>{ui.noInspectionHelp}</p>
            <button type="button" onClick={() => setInspectionOpen(true)} title={ui.openInspection}>
              <Menu size={18} />
              {ui.openInspection}
            </button>
          </section>
        )}

        {previewOpen && activeAudit && (
          <div className="preview-backdrop" role="dialog" aria-modal="true">
            <div className="preview-panel">
              <div className="preview-head">
                <div>
                  <span className="eyebrow">{ui.previewQuestionnaire}</span>
                  <h2>{activeTemplate.title}</h2>
                  <p>{activeAudit.title}</p>
                </div>
                <button type="button" onClick={() => setPreviewOpen(false)} title={ui.close}>
                  {ui.close}
                </button>
              </div>
              <ReportPreview audit={{ ...activeAudit, values: effectiveValues }} template={activeTemplate} auditor={activeAreaAuditor} assignment={activeAssignment} ui={ui} language={language} />
            </div>
          </div>
        )}

        {normDialog && (
          <div className="preview-backdrop" role="dialog" aria-modal="true">
            <div className="preview-panel norm-dialog">
              <div className="preview-head">
                <div>
                  <span className="eyebrow">{ui.normReference}</span>
                  <h2>{normDialog.reference?.code || normDialog.field?.normRef || ui.normativeReferences}</h2>
                  <p>{normDialog.reference?.title || normDialog.field?.label}</p>
                </div>
                <button type="button" onClick={() => setNormDialog(null)} title={ui.close}>
                  {ui.close}
                </button>
              </div>
              <div className="norm-detail">
                {normDialog.reference && (
                  <>
                    <div>
                      <span>{ui.allCategories}</span>
                      <strong>{normDialog.reference.category.replace("_", " / ")} - {normDialog.reference.level}</strong>
                    </div>
                    <p>{normDialog.reference.description}</p>
                  </>
                )}
                {normDialog.field?.guidance && (
                  <div>
                    <span>{ui.guidance}</span>
                    <strong>{normDialog.field.guidance}</strong>
                  </div>
                )}
                {normDialog.field && (
                  <div>
                    <span>{ui.linkedFields}</span>
                    <strong>{normDialog.field.label}</strong>
                  </div>
                )}
                {normDialog.reference && linkedFieldsByNorm(normDialog.reference).length > 0 && (
                  <div>
                    <span>{ui.linkedFields}</span>
                    <ul className="linked-field-list norm-dialog-links">
                      {linkedFieldsByNorm(normDialog.reference).map((fieldLink) => (
                        <li key={`${fieldLink.templateId}-${fieldLink.sectionId}-${fieldLink.fieldId}`}>
                          <button type="button" onClick={() => openLinkedField(fieldLink)} title={`${ui.openLinkedField}: ${fieldLink.fieldLabel}`}>
                            <span>{fieldLink.templateTitle}</span>
                            <small>{fieldLink.sectionTitle}</small>
                            <strong>{fieldLink.fieldLabel}</strong>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {(normDialog.field?.sourceUrl || normDialog.reference?.url) && (
                  <a href={normDialog.field?.sourceUrl || normDialog.reference?.url} target="_blank" rel="noreferrer" className="norm-source-link">
                    <ExternalLink size={16} />
                    {ui.sourceUrl}
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {view === "reports" && (
          <section className="grid">
            {inspectionTemplates.map((template) => {
              const record = audits.find((audit) => audit.templateId === template.id && audit.assignmentId === activeAssignment.id);
              return (
                <div className="panel finding" key={template.id}>
                  <strong>{template.menuLabel ?? template.title} Report</strong>
                  <p>{ui.dedicatedReportBody}</p>
                  <button onClick={() => openTemplate(template)} title={ui.openQuestionnaire}>{ui.openQuestionnaire}</button>
                  <button
                    title={ui.reportPreview}
                    onClick={() => {
                      if (record) setActiveId(record.id);
                      else createRecord(template);
                      setPreviewOpen(true);
                    }}
                  >
                    {ui.reportPreview}
                  </button>
                </div>
              );
            })}
          </section>
        )}

        {view === "settings" && (
          <section className="settings-panel">
            <div className="panel-title">
              <Settings />
              <h2>{ui.auditorSettings}</h2>
            </div>
            <div className="settings-language">
              <div>
                <strong>{ui.languageSettings}</strong>
                <span>{ui.platformLanguage}</span>
              </div>
              <select value={language} onChange={(event) => updateLanguage(event.target.value as AppLanguage)} title={ui.platformLanguage}>
                <option value="en">{ui.english}</option>
                <option value="it">{ui.italian}</option>
              </select>
            </div>
            <div className="settings-language">
              <div>
                <strong>{ui.onlineSettings}</strong>
                <span>{ui.onlineSettingsBody}</span>
                <small>{cloudStatus}{cloudUser ? ` · ${cloudUser}` : ""}</small>
              </div>
              <div className="settings-stack">
                <select value={appSettings.cloudMode} onChange={(event) => updateOnlineSettings({ cloudMode: event.target.value as AppSettings["cloudMode"] })} title={ui.cloudMode}>
                  <option value="local-first">{ui.localFirstMode}</option>
                  <option value="cloud-online">{ui.cloudOnlineMode}</option>
                </select>
                <select value={appSettings.authMode} onChange={(event) => updateOnlineSettings({ authMode: event.target.value as AppSettings["authMode"] })} title={ui.authMode}>
                  <option value="cloudflare-access">{ui.cloudflareAccessMode}</option>
                  <option value="app-login">{ui.appLoginMode}</option>
                </select>
              </div>
            </div>
            <div className="repository-panel">
              <div className="panel-title compact-title">
                <Database />
                <div>
                  <h2>{ui.externalRepositories}</h2>
                  <p>{ui.externalRepositoriesBody}</p>
                </div>
              </div>
              <div className="crud-bar repository-crud">
                <input value={activeRepository.label} onChange={(event) => updateRepository({ label: event.target.value })} aria-label={ui.externalRepositories} />
                <select
                  value={activeRepository.provider}
                  onChange={(event) => {
                    const provider = event.target.value as RepositoryProvider;
                    updateRepository({ provider, label: activeRepository.label || repositoryProviderLabel(provider), status: "Needs configuration" });
                  }}
                  title={ui.repositoryProvider}
                >
                  {repositoryProviders.map((provider) => (
                    <option key={provider} value={provider}>
                      {repositoryProviderLabel(provider)}
                    </option>
                  ))}
                </select>
                <button type="button" onClick={createRepository} title={ui.createRepository}>
                  <Plus size={18} />
                  {ui.newButton}
                </button>
                <button type="button" onClick={duplicateRepository} title={ui.duplicateRepository}>
                  <Copy size={18} />
                  {ui.duplicate}
                </button>
                <button type="button" onClick={deleteRepository} title={ui.deleteRepository}>
                  <Trash2 size={18} />
                  {ui.delete}
                </button>
                <button type="button" onClick={saveRepository} title={ui.saveRepository}>
                  <Save size={18} />
                  {ui.save}
                </button>
                <button type="button" onClick={connectRepository} title={ui.connectRepository}>
                  <ExternalLink size={18} />
                  Connect
                </button>
              </div>
              <div className="record-list repository-records" aria-label={ui.externalRepositories}>
                {appSettings.repositories.map((repository) => (
                  <button key={repository.id} type="button" className={repository.id === activeRepository.id ? "active" : ""} onClick={() => setActiveRepositoryId(repository.id)} title={repository.label}>
                    <strong>{repository.label}</strong>
                    <span>{repositoryProviderLabel(repository.provider)} · {displayValue(repository.status, language)}</span>
                    <small>{repository.id === appSettings.primaryRepositoryId ? ui.primaryRepository : repository.savedAt ? `${ui.savedMeta} ${formatDateTime(repository.savedAt, language)}` : displayValue("Draft", language)}</small>
                  </button>
                ))}
              </div>
              <div className="field-grid">
                <label>
                  <span>{ui.primaryRepository}</span>
                  <select value={appSettings.primaryRepositoryId} onChange={(event) => updateOnlineSettings({ primaryRepositoryId: event.target.value })}>
                    {appSettings.repositories.map((repository) => (
                      <option key={repository.id} value={repository.id}>
                        {repository.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>{ui.repositoryStatus}</span>
                  <select value={activeRepository.status} onChange={(event) => updateRepository({ status: event.target.value as RepositoryStatus })}>
                    {repositoryStatuses.map((status) => (
                      <option key={status} value={status}>
                        {displayValue(status, language)}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>{ui.repositoryRootFolder}</span>
                  <input value={activeRepository.rootFolder} onChange={(event) => updateRepository({ rootFolder: event.target.value })} />
                </label>
                <label>
                  <span>{ui.repositoryAccount}</span>
                  <input type="email" value={activeRepository.accountEmail} onChange={(event) => updateRepository({ accountEmail: event.target.value })} />
                </label>
                <label>
                  <span>{ui.repositoryClientId}</span>
                  <input value={activeRepository.clientId} onChange={(event) => updateRepository({ clientId: event.target.value, status: "Needs configuration" })} />
                </label>
                <label>
                  <span>{ui.repositoryRedirect}</span>
                  <input readOnly value={`${window.location.origin}/api/repositories/oauth/callback`} />
                </label>
              </div>
            </div>
            <div className="crud-bar auditor-crud">
              <input value={auditor.label} onChange={(event) => updateAuditor({ label: event.target.value })} aria-label={ui.auditorSettings} />
              <button type="button" onClick={createAuditor} title={ui.createAuditor}>
                <Plus size={18} />
                {ui.newButton}
              </button>
              <button type="button" onClick={duplicateAuditor} title={ui.duplicateAuditor}>
                <Copy size={18} />
                {ui.duplicate}
              </button>
              <button type="button" onClick={deleteAuditor} title={ui.deleteAuditor}>
                <Trash2 size={18} />
                {ui.delete}
              </button>
              <button type="button" onClick={saveAuditorSettings} title={ui.saveAuditor}>
                <Save size={18} />
                {ui.save}
              </button>
            </div>
            <div className="record-list auditor-records" aria-label={ui.auditorProfiles}>
              {auditorProfiles.map((profile) => (
                <button key={profile.id} type="button" className={profile.id === auditor.id ? "active" : ""} onClick={() => setActiveAuditorId(profile.id)} title={`${ui.openAuditor} ${profile.label || ""}`}>
                  <strong>{profile.label || "Auditor"}</strong>
                  <span>{[profile.firstName, profile.lastName].filter(Boolean).join(" ") || ui.noName}</span>
                  <small>{profile.savedAt ? `${ui.savedMeta} ${formatDateTime(profile.savedAt, language)}` : displayValue("Draft", language)}</small>
                </button>
              ))}
            </div>
            <div className="field-grid">
              <label>
                <span>{ui.firstName}</span>
                <input value={auditor.firstName} onChange={(event) => updateAuditor({ firstName: event.target.value })} />
              </label>
              <label>
                <span>{ui.lastName}</span>
                <input value={auditor.lastName} onChange={(event) => updateAuditor({ lastName: event.target.value })} />
              </label>
              <label>
                <span>{ui.phone}</span>
                <input value={auditor.phone} onChange={(event) => updateAuditor({ phone: event.target.value })} />
              </label>
              <label>
                <span>Email</span>
                <input type="email" value={auditor.email} onChange={(event) => updateAuditor({ email: event.target.value })} />
              </label>
            </div>
          </section>
        )}

        {view === "sync" && (
          <section className="grid">
            {[
              ["Worker API", "CRUD audit, persone, siti, processi, asset, documenti, evidenze, report e CAPA."],
              ["D1", "Database relazionale primario multi-tenant."],
              ["R2", "Media originali, allegati, foto, audio e video con accesso firmato."],
              ["KV", "Configurazioni, lookup, feature flag e metadati leggeri."],
              ["Durable Objects", "Coordinamento sessione, lock e sync offline."],
              ["Queues + Workflows", "Report, estrazione metadati, classificazioni e follow-up long-running."],
              ["Workers AI + Vectorize", "Trascrizioni, ricerca semantica, clustering evidenze e assistenza report."],
              ["Zero Trust + Turnstile", "Accesso amministrativo, protezione login/form e rate limiting."]
            ].map(([title, body]) => (
              <div className="panel cloud" key={title}>
                <CheckCircle2 />
                <h2>{title}</h2>
                <p>{body}</p>
              </div>
            ))}
            <div className="panel span-3 command-panel">
              <h2>{ui.commands}</h2>
              <code>npm run build</code>
              <code>npm run cf:dev</code>
              <code>npm run cf:deploy:dry-run</code>
              <button onClick={() => window.location.reload()} title={ui.reloadApp}>
                <RefreshCw size={18} />
                {ui.reloadApp}
              </button>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
